import React, { useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Button, Alert, AlertTitle, ButtonGroup } from "@mui/material";
import { PlayArrow as PlayIcon, Stop as StopIcon } from "@mui/icons-material";
import { mapToDisplay } from "../CameraNames";

import LiveVideoPlayerHtml5 from "./LiveVideoPlayerHtml5";
import LiveVideoPlayerIframe from "./LiveVideoPlayerIframe";
import LiveVideoWebSocketCmsv6 from "./LiveVideoWebSocketCmsv6";
import LiveVideoPlayerNexusWebRtc from "./LiveVideoPlayerNexusWebRtc";
import {
  playerKindForCamera,
  createStaggeredPlayScheduler,
} from "../liveVideoPlayback";
import useIdleStop from "./useIdleStop";

const IDLE_TIMEOUT = 2 * 60 * 1000;
const IDLE_WARNING = 30 * 1000;

const PLAY_ALL_STAGGER_MS = 400;

const PLAYER_BY_KIND = {
  "nexus-webrtc": LiveVideoPlayerNexusWebRtc,
  websocket: LiveVideoWebSocketCmsv6,
  html5: LiveVideoPlayerHtml5,
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "grid",
    gridGap: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: `repeat(auto-fit, minmax(190px, 1fr))`,
    },
    [theme.breakpoints.up("lg")]: {
      gridTemplateColumns: `repeat(auto-fit, minmax(220px, 1fr))`,
    },
  },
  videoControlContainer: {
    marginBottom: theme.spacing(2),
  },
}));

const LiveVideo = ({ streams, vehicleId, onSetCameraLocationClick }) => {
  const isLoadVideoIframe = streams.some((camera) => {
    return camera.streams.find((x) => x.type === "iframe");
  });

  const classes = useStyles();
  const [userIdle, setUserIdle] = React.useState(false);

  const [playing, setPlaying] = React.useState({});
  const schedulerRef = React.useRef(null);
  if (!schedulerRef.current) {
    schedulerRef.current = createStaggeredPlayScheduler({
      staggerMs: PLAY_ALL_STAGGER_MS,
      onPlay: (idx) => setPlaying((prev) => ({ ...prev, [idx]: true })),
    });
  }

  const onUserIdle = () => {
    schedulerRef.current.cancelAll();
    setUserIdle(true);
  };

  const onUserActive = () => {
    setUserIdle(false);
    const wasPlaying = Object.keys(playing)
      .filter((key) => playing[key])
      .map(Number);
    if (wasPlaying.length > 0) {
      setPlaying({});
      schedulerRef.current.schedule(wasPlaying);
    }
  };

  const idleSecondsLeft = useIdleStop({
    timeout: IDLE_TIMEOUT,
    warnFor: IDLE_WARNING,
    onStop: onUserIdle,
  });
  const anyPlaying = Object.values(playing).some(Boolean);

  const handleSetCameraLocationClick = ({
    cameraId,
    camera: existingLocation,
  }) => {
    onSetCameraLocationClick({ cameraId, existingLocation });
  };

  const handlePlayClick = (idx) => {
    setPlaying((prev) => ({ ...prev, [idx]: true }));
  };

  const handleStopClick = (idx) => {
    schedulerRef.current.cancel(idx);
    setPlaying((prev) => ({ ...prev, [idx]: false }));
  };

  const handlePlayAllClick = () => {
    schedulerRef.current.schedule(streams.map((_, idx) => idx));
  };

  const handleStopAllClick = () => {
    schedulerRef.current.cancelAll();
    setAllPlayersPlaying(false);
  };

  const setAllPlayersPlaying = (playing) => {
    setPlaying(
      streams.reduce(
        (agg, _, idx) => ({
          ...agg,
          [idx]: playing,
        }),
        {}
      )
    );
  };

  // Reset players only when the actual set of cameras changes, keyed by cameraId — NOT on the
  // `streams` array reference. That reference changes on every live-status poll (VehicleLiveMap polls
  // Vehicle.live every 10s, so Apollo hands back a fresh live.video array even when the cameras are
  // unchanged); depending on it stopped every player each poll, tearing the WebRTC session down and
  // restarting it in a loop (~5-10s cycle) so live video never stabilised.
  const cameraKey = streams.map((camera) => camera.cameraId).join(",");
  useEffect(() => {
    return () => {
      schedulerRef.current.cancelAll();
      setAllPlayersPlaying(false);
    };
  }, [cameraKey]);

  return (
    <>
      {!userIdle && anyPlaying && idleSecondsLeft !== null && (
        <Alert severity="warning">
          Live video will stop in {idleSecondsLeft}s due to inactivity. Move
          the mouse to keep watching.
        </Alert>
      )}
      {userIdle && (
        <div>
          <Alert severity="info">
            <AlertTitle>Live stream stopped</AlertTitle>
            <div>
              <p>Live video streams have been stopped due to inactivity.</p>
              <Button variant="outlined" onClick={onUserActive}>
                Click here to resume
              </Button>
            </div>
          </Alert>
        </div>
      )}
      {!userIdle && !isLoadVideoIframe && (
        <div className="">
          <div className={classes.videoControlContainer}>
            <ButtonGroup
              variant="outlined"
              size="small"
              aria-label="outlined button group"
            >
              <Button startIcon={<PlayIcon />} onClick={handlePlayAllClick}>
                Play all
              </Button>
              <Button startIcon={<StopIcon />} onClick={handleStopAllClick}>
                Stop all
              </Button>
            </ButtonGroup>
          </div>

          <div className={classes.root}>
            {streams.map((camera, idx) => {
              const LiveVideoPlayer = PLAYER_BY_KIND[playerKindForCamera(camera)];
              return (
                <div key={`video-${idx}`}>
                  <LiveVideoPlayer
                    idx={idx}
                    label={mapToDisplay(camera.camera)}
                    sources={camera.streams}
                    vehicleId={vehicleId}
                    cameraId={camera.cameraId}
                    onSetCameraLocationClick={() =>
                      handleSetCameraLocationClick(camera)
                    }
                    play={playing[idx]}
                    onPlayClick={() => handlePlayClick(idx)}
                    onStopClick={() => handleStopClick(idx)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!userIdle && isLoadVideoIframe && (
        <div className="">
          <div className={classes.root}>
            {streams.map((camera, idx) => (
              <LiveVideoPlayerIframe
                key={`video-${idx}`}
                label={camera.label}
                sources={camera.streams}
                onSetCameraLocationClick={() =>
                  handleSetCameraLocationClick(camera)
                }
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default LiveVideo;
